import React, { useEffect, useState, useContext } from "react";
import {
  Modal,
  Button,
  Card,
  ListGroup,
  ListGroupItem,
  Table,
  Badge,
} from "react-bootstrap";
import VisaImg from "../../assets/images/visaimg.png";
import MasterCardImg from "../../assets/images/mastercard.png";
import { format } from "date-fns";
import { useUserState, UserContext } from "../../Contexts/UserContext";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "./Loader";
import PlusSvg from "../../assets/images/plus.svg";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Typography } from "antd";
import MyCard from "../furniture/myBilling/MyCard";
import BankAccountManager from "../furniture/myBilling/BankAccountManager";
import { buildFinalPaymentPayload } from "../../utils/paymentDistributionHelper";

const { Text } = Typography;
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const ModalComponent = ({
  showModal,
  setShowModal,
  handleCloseModal,
  cardData,
  FetchCardData,
  totalAmount,
  cartId,
  subscription_type,
  housePrice,
  setIsDisabledButton,
  monthlypay,
  paymentBreakdown
}) => {
  const { user, isLoggedIn } = useUserState();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loader, setLoader] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAddBankModal, setShowAddBankModal] = useState(false);

  const [paymentType, setPaymentType] = useState("bank"); // Default to bank (eCheck)
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  const [title, setTitle] = useState("Select Payment Method");
  const [selectedCardOption, setSelectedCardOption] = useState(-1);
  const [selectedCard, setSelectedCard] = useState(null);
  const [errMessage, setErrMessage] = useState("");
  const { setExpiryTiming } = useContext(UserContext);

  const headers = {
    Authorization: user?.token,
  };

  useEffect(() => {
    if (user?.token && paymentType === "bank" && showModal) {
      fetchBankAccounts();
    }
  }, [user?.token, paymentType, showModal]);

  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${import.meta.env.VITE_APP_BASE_URL}/api/payment-method/get-bank-accounts`, {
        headers,
      });
      if (res.data?.success || res.data?.statusCode === 200) {
        const accounts = res.data.data || [];
        setBankAccounts(accounts);

        if (!selectedBankAccount && accounts.length > 0) {
          const defaultBank = accounts.find(b => b.is_default) || accounts[0];
          setSelectedBankAccount(defaultBank);
        }
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
    }
  };

  const FetchData = async (url, data) => {
    const baseCartUrl = `${import.meta.env.VITE_APP_BASE_URL}${url}`;
    try {
      const response = await axios.post(baseCartUrl, data, { headers });
      if (response.status === 200) {
        return response.data.data;
      }
    } catch (error) {
      console.error("Error during API request:", error);
      throw error;
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();

    const selectedPaymentMethod = paymentType === "bank"
      ? selectedBankAccount?.stripe_payment_method_id
      : selectedCard?.id;

    if (!selectedPaymentMethod) {
      setErrMessage(`Please select a ${paymentType === "bank" ? "bank account" : "card"}!!`);
      return;
    }

    setLoader(true);
    setErrMessage("");

    try {
      const createRes = await FetchData("/api/payment-method/create", {
        paymentMethod: selectedPaymentMethod,
        amount: parseFloat(totalAmount).toFixed(2),
        cartId: cartId,
      });

      if (!createRes || !createRes.id) {
        throw new Error("Failed to create payment intent");
      }

      // Build enriched monthlyPay with distribution_data per schedule
      const enrichedMonthlyPay = subscription_type === "monthly"
        ? (paymentBreakdown?.monthlySchedules || []).map((schedule, index) => ({
            month: schedule.month,
            amount: monthlypay?.[index + 1]?.amount || schedule.amount,
            distribution_data: schedule.distribution_data,
          }))
        : [];

      const payload = buildFinalPaymentPayload({
        paymentIntent: createRes.id,
        paymentMethod: selectedPaymentMethod,
        subscriptionType: subscription_type,
        housePrice: housePrice,
        initialDistribution: paymentBreakdown?.firstPaymentDistribution || {},
        monthlySchedules: enrichedMonthlyPay,
      });

      const confirmRes = await FetchData("/api/payment-method/confirm", payload);

      const SUCCESS_STATUSES = ["succeeded", "processing", "requires_action"];

      if (confirmRes && SUCCESS_STATUSES.includes(confirmRes.status)) {
        setLoader(false);
        setExpiryTiming(true);
        navigate("/thanks");
        window.location.reload();
      } else {
        throw new Error(`Payment status: ${confirmRes?.status || "failed"}`);
      }
    } catch (error) {
      console.error(error);
      const stripeMessage =
        error?.response?.data?.data?.raw?.message ||
        error?.response?.data?.message ||
        error.message ||
        "Payment failed. Please try again.";

      setErrMessage(stripeMessage);
      setLoader(false);
    }
  };

  const handleAddCard = (e) => {
    e.preventDefault();
    setShowModal(false);
    setShowAddCardModal(true);
  };

  const handleAddBank = (e) => {
    e.preventDefault();
    setShowModal(false);
    setShowAddBankModal(true);
  };

  const getPaymentDetails = () => {
    setShowAddCardModal(false);
    setShowAddBankModal(false);
    setShowModal(true);
    if (paymentType === "bank") fetchBankAccounts();
    else FetchCardData();
  };

  const handleOptionChange = (selectedOption, card) => {
    setErrMessage("");
    setSelectedCardOption(selectedOption);
    setSelectedCard(card);
  };

  const handleBankOptionChange = (bank) => {
    setErrMessage("");
    setSelectedBankAccount(bank);
  };

  useEffect(() => {
    setIsDisabledButton(loader)
  }, [loader])

  return (
    <>
      <style>
        {`
                .payment-selector {
                  display: flex;
                  gap: 10px;
                  margin-bottom: 20px;
                  padding: 5px;
                  background: #f0f2f5;
                  border-radius: 8px;
                }
                .payment-selector button {
                  flex: 1;
                  padding: 10px;
                  border: none;
                  background: none;
                  border-radius: 6px;
                  font-weight: 600;
                  transition: all 0.3s;
                }
                .payment-selector button.active {
                  background: #fff;
                  color: #0474ba;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                .bank-account-item {
                  border: 1px solid #e8e8e8;
                  border-radius: 8px;
                  padding: 12px;
                  margin-bottom: 10px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  transition: all 0.3s;
                }
                .bank-account-item:hover {
                  border-color: #0474ba;
                  background: #f0f7ff;
                }
                .bank-account-item.active {
                  border-color: #0474ba;
                  background: #e6f7ff;
                }
                .payment-label {
                  cursor: pointer;
                  display: flex;
                  justify-content: space-between;
                  width: 100%;
                  margin: 0;
                  padding: 10px;
                }
                .payment {
                  border: 1px solid #e8e8e8;
                  border-radius: 8px;
                  padding: 12px 15px;
                  margin-bottom: 10px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  gap: 15px;
                  transition: all 0.3s;
                }
                .payment:hover {
                  border-color: #0474ba;
                  background: #f0f7ff;
                }
                .payment.active {
                   border-color: #0474ba;
                   background: #e6f7ff;
                }
                .payment-info {
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                }
        `}
      </style>
      {loader ? (
        <></>
      ) : (
        <>
          <Modal
            show={showModal}
            backdrop="static"
            enforceFocus={false}
            onHide={() => {
              handleCloseModal();
              setErrMessage("");
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>Select Payment Method</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="payment-selector">
                <button
                  className={paymentType === "bank" ? "active" : ""}
                  onClick={() => { setPaymentType("bank"); setErrMessage(""); }}
                >
                  eCheck
                </button>
                <button
                  className={paymentType === "card" ? "active" : ""}
                  onClick={() => { setPaymentType("card"); setErrMessage(""); }}
                >
                  Debit Card
                </button>
              </div>

              <div className="wrapper">
                {paymentType === "bank" ? (
                  <div className="step-container">
                    <div className="step-body">
                      {bankAccounts.length > 0 ? (
                        <div className="bank-accounts">
                          {bankAccounts.map((bank) => (
                            <div
                              key={bank.id}
                              className={`bank-account-item ${selectedBankAccount?.id === bank.id ? "active" : ""}`}
                              onClick={() => handleBankOptionChange(bank)}
                            >
                              <div style={{ fontSize: "24px", color: "#0474ba" }}>
                                <i className="fa fa-university"></i>
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                  <Text strong style={{ fontWeight: 600 }}>{bank.bank_name || "Bank Account"}</Text>
                                  {bank.is_default && <Badge bg="primary">Default</Badge>}
                                </div>
                                <Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
                                  **** {bank.last4} | {bank.account_holder_name} ({bank.account_type})
                                </Text>
                              </div>
                              <input
                                type="radio"
                                checked={selectedBankAccount?.id === bank.id}
                                readOnly
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <Text type="secondary">No saved bank accounts found.</Text>
                        </div>
                      )}

                      <div className="text-center mt-3">
                        <Button
                          variant="outline-primary"
                          className="w-100"
                          onClick={handleAddBank}
                          style={{ borderStyle: "dashed" }}
                        >
                          + USE A NEW BANK ACCOUNT
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="step-container">
                    <div className="step-body">
                      <form className="payments">
                        {cardData?.length > 0 ? (
                          cardData.map((card, i) => (
                            <div
                              key={i}
                              className={`payment ${selectedCardOption === i ? "active" : ""}`}
                              onClick={() => handleOptionChange(i, card)}
                              style={{ display: "flex", alignItems: "center", gap: "15px" }}
                            >
                              <div style={{ width: "45px", textAlign: "center", flexShrink: 0 }}>
                                <img
                                  src={
                                    card.card.brand === "visa"
                                      ? "https://2.bp.blogspot.com/-E85z2lvyugo/Wxqgevf3MLI/AAAAAAAAA5Y/vfTQDWg8g9Ii--s6KhDC0yWb9_h7l47dwCLcBGAs/s1600/visa.png"
                                      : "https://1.bp.blogspot.com/-b_7SEUzJd4M/Wxqgerdr6xI/AAAAAAAAA5c/7b1znN3spZsGJUn7DlQh72hzbQoqmYEtACLcBGAs/s1600/mastercard.png"
                                  }
                                  alt={card.card.brand}
                                  style={{ width: "100%", opacity: 0.9, objectFit: "contain" }}
                                />
                              </div>
                              <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                <Text strong style={{ fontWeight: 600, display: "block", lineHeight: "1.2", marginBottom: "4px" }}>
                                  {card.billing_details.name || "Card Holder"}
                                </Text>
                                <Text type="secondary" style={{ fontSize: "13px", display: "block", lineHeight: "1.2" }}>
                                  **** **** **** {card.card.last4}
                                </Text>
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <input
                                  type="radio"
                                  checked={selectedCardOption === i}
                                  readOnly
                                  style={{ cursor: "pointer", margin: 0, display: "block" }}
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4">
                            <Text type="secondary">No saved cards found.</Text>
                          </div>
                        )}
                      </form>
                      <div className="text-center mt-3">
                        <Button
                          variant="outline-primary"
                          className="w-100"
                          onClick={handleAddCard}
                          style={{ borderStyle: "dashed" }}
                        >
                          + USE A NEW CARD
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {errMessage && (
                  <p className="text-danger text-center mt-3">{errMessage}</p>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button className="druyp-outline" onClick={handleCloseModal}>
                Close
              </button>
              <button className="druyp-btn" onClick={handleConfirm} disabled={loader}>
                Confirm Payment
              </button>
            </Modal.Footer>
          </Modal>

          {/* Add Card Modal */}
          <Modal
            show={showAddCardModal}
            onHide={() => setShowAddCardModal(false)}
            size="lg"
            enforceFocus={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Add New Card</Modal.Title>
            </Modal.Header>
            <div className="modal-body">
              <Elements stripe={stripePromise}>
                <MyCard
                  token={user?.token}
                  setTitle={setTitle}
                  getPaymentDetails={getPaymentDetails}
                />
              </Elements>
            </div>
          </Modal>

          {/* Add Bank Modal */}
          <Modal
            show={showAddBankModal}
            onHide={() => setShowAddBankModal(false)}
            size="lg"
            enforceFocus={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Add Bank Account</Modal.Title>
            </Modal.Header>
            <div className="modal-body">
              <Elements stripe={stripePromise}>
                <BankAccountManager
                  role="tenant"
                  user={user}
                  headerJson={{ Authorization: user?.token }}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                />
                <div className="text-center mt-4">
                  <Button variant="link" onClick={getPaymentDetails}>
                    Back to Selection
                  </Button>
                </div>
              </Elements>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default ModalComponent;
